class ImageReveal extends HTMLElement {
  constructor() {
    super();
    this.observer = null;
    this.hasRevealed = false;
  }

  connectedCallback() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.classList.add('ai-revealed');
      return;
    }

    const threshold = parseFloat(this.dataset.threshold ?? '0.2') || 0.2;
    const delay = parseInt(this.dataset.delay ?? '0') || 0;

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.hasRevealed) {
            if (delay > 0) {
              setTimeout(() => {
                this.reveal();
              }, delay);
            } else {
              this.reveal();
            }
          }
        });
      },
      {
        threshold: threshold,
        rootMargin: '0px'
      }
    );

    this.observer.observe(this);
  }

  reveal() {
    this.classList.add('ai-revealed');
    this.hasRevealed = true;

    if (this.observer) {
      this.observer.disconnect();
    }
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }
}

class StaggerGroup extends HTMLElement {
  constructor() {
    super();
    this.observer = null;
    this.hasRevealed = false;
    this.timeoutIds = [];
  }

  connectedCallback() {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const designMode = !!(window.Shopify && window.Shopify.designMode);

    if (reduceMotion || designMode) {
      this.revealImmediately();
      return;
    }

    const threshold = parseFloat(this.dataset.threshold ?? '0.2') || 0.2;
    const delay = parseInt(this.dataset.delay ?? '0') || 0;

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.hasRevealed) {
            if (delay > 0) {
              const timeoutId = setTimeout(() => {
                this.reveal();
              }, delay);
              this.timeoutIds.push(timeoutId);
            } else {
              this.reveal();
            }
          }
        });
      },
      {
        threshold: threshold,
        rootMargin: '0px'
      }
    );

    this.observer.observe(this);
  }

  revealImmediately() {
    this.classList.add('stagger-revealed');
    this.hasRevealed = true;
    const contentWrapper = this.querySelector('.group-block-content');
    if (contentWrapper) {
      Array.from(contentWrapper.children).forEach((item) => {
        item.classList.add('stagger-item-revealed');
      });
    }
  }

  reveal() {
    this.classList.add('stagger-revealed');
    this.hasRevealed = true;

    const staggerDelay = parseInt(this.dataset.staggerDelay ?? '100') || 100;
    const contentWrapper = this.querySelector('.group-block-content');
    
    if (contentWrapper) {
      const items = Array.from(contentWrapper.children);
      items.forEach((item, index) => {
        const itemDelay = index * staggerDelay;
        const timeoutId = setTimeout(() => {
          item.classList.add('stagger-item-revealed');
        }, itemDelay);
        this.timeoutIds.push(timeoutId);
      });
    }

    if (this.observer) {
      this.observer.disconnect();
    }
  }

  disconnectedCallback() {
    // Clear all pending timeouts to prevent memory leaks
    this.timeoutIds.forEach((id) => clearTimeout(id));
    this.timeoutIds = [];

    if (this.observer) {
      this.observer.disconnect();
    }
  }
}

if (!customElements.get('image-reveal')) {
  customElements.define('image-reveal', ImageReveal);
}

if (!customElements.get('stagger-group')) {
  customElements.define('stagger-group', StaggerGroup);
}
